<!-- eslint-disable -->
<template>
  <v-form @submit.prevent="submitPost" ref="formRef">
    <v-textarea
      v-model="postContent"
      :placeholder="$t('wall.placeholder')"
      variant="outlined"
      rows="4"
      counter="5000"
      :rules="[rules.maxLength]"
      :autofocus="autofocus"
      ref="postTextarea"
      class="mb-4"
      @keydown="handlePostKeydown"
    ></v-textarea>

    <v-select
      v-if="!groupId"
      v-model="selectedGroup"
      :items="groupOptions"
      item-title="groupName"
      item-value="groupId"
      :label="$t('wall.group')"
      variant="outlined"
      clearable
      class="mb-4"
    ></v-select>

    <!-- Multi-image upload -->
    <v-file-input
      v-model="selectedImages"
      :label="$t('wall.selectImages')"
      accept="image/*"
      prepend-icon="mdi-camera"
      variant="outlined"
      :hint="$t('wall.imageHint', { max: 20, size: 10 })"
      persistent-hint
      multiple
      show-size
      counter
      :rules="[rules.maxFiles, rules.maxFileSize]"
      class="mb-2"
      @update:model-value="handleImagesSelected"
    ></v-file-input>

    <!-- Image previews -->
    <div v-if="imagePreviews.length > 0" class="image-previews mb-4">
      <div class="preview-grid">
        <div
          v-for="(preview, index) in imagePreviews"
          :key="index"
          class="preview-item"
        >
          <v-img
            :src="preview.url"
            aspect-ratio="1"
            cover
            class="rounded"
          ></v-img>
          <v-btn
            icon
            size="x-small"
            color="error"
            class="remove-btn"
            @click="removeImage(index)"
          >
            <v-icon size="small">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      <p class="text-caption text-grey mt-1">
        {{ $t('wall.imagesSelected', { count: imagePreviews.length }) }}
      </p>
    </div>

    <!-- Upload progress -->
    <v-progress-linear
      v-if="uploadProgress > 0 && uploadProgress < 100"
      :model-value="uploadProgress"
      color="primary"
      height="8"
      rounded
      class="mb-4"
    >
      <template v-slot:default>
        <span class="text-caption">{{ uploadProgress }}%</span>
      </template>
    </v-progress-linear>

    <v-select
      v-model="visibility"
      :items="visibilityOptions"
      :label="$t('wall.visibility')"
      variant="outlined"
      class="mb-4"
    ></v-select>

    <!-- Poll toggle -->
    <div class="d-flex align-center mb-1">
      <v-btn
        variant="text"
        size="small"
        :color="showPoll ? 'primary' : 'default'"
        prepend-icon="mdi-poll"
        @click="showPoll = !showPoll"
      >
        {{ showPoll ? $t('poll.removePoll') : $t('poll.addPoll') }}
      </v-btn>
    </div>

    <v-expand-transition>
      <div v-if="showPoll" class="poll-builder pa-3 mt-1 mb-3">

        <div class="text-caption text-grey mb-2">{{ $t('poll.type') }}</div>
        <v-btn-toggle
          v-model="pollType"
          mandatory
          density="compact"
          variant="outlined"
          class="mb-3"
        >
          <v-btn value="single" size="small" prepend-icon="mdi-radiobox-marked">
            {{ $t('poll.singleChoice') }}
          </v-btn>
          <v-btn value="multiple" size="small" prepend-icon="mdi-checkbox-marked-outline">
            {{ $t('poll.multipleChoice') }}
          </v-btn>
          <v-btn value="yes_no" size="small" prepend-icon="mdi-thumb-up-down">
            {{ $t('poll.yesNo') }}
          </v-btn>
        </v-btn-toggle>

        <v-text-field
          v-model="pollQuestion"
          :label="$t('poll.question')"
          variant="outlined"
          density="compact"
          counter="200"
          maxlength="200"
          class="mb-3"
        />

        <!-- Custom options (hidden for yes_no) -->
        <template v-if="!isYesNo">
          <div class="text-caption text-grey mb-2">{{ $t('poll.options') }}</div>
          <div
            v-for="(_, i) in pollOptions"
            :key="i"
            class="d-flex align-center mb-2"
            style="gap: 0.5rem;"
          >
            <v-text-field
              v-model="pollOptions[i]"
              :label="`${$t('poll.option')} ${i + 1}`"
              variant="outlined"
              density="compact"
              hide-details
              maxlength="200"
              style="flex: 1;"
            />
            <v-btn
              v-if="pollOptions.length > 2"
              icon="mdi-close"
              size="small"
              variant="text"
              color="grey"
              @click="pollOptions.splice(i, 1)"
            />
          </div>
          <v-btn
            v-if="pollOptions.length < 10"
            variant="text"
            size="small"
            prepend-icon="mdi-plus"
            color="primary"
            @click="pollOptions.push('')"
          >
            {{ $t('poll.addOption') }}
          </v-btn>
        </template>

        <!-- Yes/No preview -->
        <template v-else>
          <div class="d-flex" style="gap: 0.5rem;">
            <v-chip prepend-icon="mdi-thumb-up" color="success" variant="tonal" size="small">
              {{ $t('poll.yes') }}
            </v-chip>
            <v-chip prepend-icon="mdi-thumb-down" color="error" variant="tonal" size="small">
              {{ $t('poll.no') }}
            </v-chip>
          </div>
        </template>

        <v-text-field
          v-model="pollExpiry"
          :label="$t('poll.expiresAt')"
          type="datetime-local"
          variant="outlined"
          density="compact"
          clearable
          hide-details
          class="mt-3"
        />
      </div>
    </v-expand-transition>

    <v-btn
      type="submit"
      color="primary"
      block
      size="large"
      :loading="submitting"
      :disabled="!canSubmit"
    >
      {{ $t('wall.send') }}
    </v-btn>
  </v-form>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import api from '../services/api'

const showPoll = ref(false)
const pollType = ref('single')
const pollQuestion = ref('')
const pollOptions = ref(['', ''])
const pollExpiry = ref('')

const isYesNo = computed(() => pollType.value === 'yes_no')

const resetPoll = () => {
  showPoll.value = false
  pollType.value = 'single'
  pollQuestion.value = ''
  pollOptions.value = ['', '']
  pollExpiry.value = ''
}

const pollPayload = computed(() => {
  if (!showPoll.value || !pollQuestion.value.trim()) return null
  const options = isYesNo.value
    ? []
    : pollOptions.value.map(o => o.trim()).filter(Boolean)
  if (!isYesNo.value && options.length < 2) return null
  return {
    question: pollQuestion.value.trim(),
    pollType: pollType.value,
    options,
    expiresAt: pollExpiry.value || null
  }
})

const props = defineProps({
  groupId: {
    type: [Number, String],
    default: null
  },
  autofocus: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['posted'])

const store = useStore()
const { t } = useI18n()

const formRef = ref(null)
const postTextarea = ref(null)
const postContent = ref('')
const selectedGroup = ref(null)
const selectedImages = ref([])
const imagePreviews = ref([])
const visibility = ref('public')
const submitting = ref(false)
const uploadProgress = ref(0)

const MAX_FILES = 20
const MAX_FILE_SIZE = 10 * 1024 * 1024

const groupOptions = computed(() => {
  const groups = store.getters['groups/myGroups'] || []
  return [
    { groupId: null, groupName: t('wall.noGroup') },
    ...groups
  ]
})

const effectiveGroupId = computed(() => {
  return props.groupId || selectedGroup.value
})

const visibilityOptions = computed(() => [
  { value: 'public', title: t('wall.public') },
  { value: 'members', title: t('wall.members') },
  { value: 'private', title: t('wall.private') }
])

const canSubmit = computed(() => {
  return (postContent.value && postContent.value.trim()) || imagePreviews.value.length > 0
})

const rules = {
  maxLength: v => !v || v.length <= 5000 || t('validation.maxLength', { max: 5000 }),
  maxFiles: v => !v || v.length <= MAX_FILES || t('validation.maxFiles', { max: MAX_FILES }),
  maxFileSize: v => {
    if (!v || v.length === 0) return true
    const oversized = v.filter(f => f.size > MAX_FILE_SIZE)
    if (oversized.length > 0) {
      return t('validation.maxFileSize', { max: '10MB' })
    }
    return true
  }
}

const handlePostKeydown = (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    submitPost()
  }
}

const focus = () => {
  postTextarea.value?.focus()
}

defineExpose({ focus })

const handleImagesSelected = (files) => {
  if (!files || files.length === 0) {
    imagePreviews.value = []
    return
  }

  imagePreviews.value = []
  for (const file of files) {
    if (file.size <= MAX_FILE_SIZE) {
      const url = URL.createObjectURL(file)
      imagePreviews.value.push({ file, url })
    }
  }
}

const removeImage = (index) => {
  URL.revokeObjectURL(imagePreviews.value[index].url)
  imagePreviews.value.splice(index, 1)

  const dt = new DataTransfer()
  imagePreviews.value.forEach(p => dt.items.add(p.file))
  selectedImages.value = Array.from(dt.files)
}

const uploadImages = async () => {
  if (imagePreviews.value.length === 0) return []

  const uploadedUrls = []
  const totalFiles = imagePreviews.value.length

  for (let i = 0; i < totalFiles; i++) {
    const { file } = imagePreviews.value[i]

    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      if (response.data.url || response.data.imageUrl) {
        uploadedUrls.push(response.data.url || response.data.imageUrl)
      }

      uploadProgress.value = Math.round(((i + 1) / totalFiles) * 100)
    } catch (error) {
      console.error(`Failed to upload image ${i + 1}:`, error)
    }
  }

  return uploadedUrls
}

const submitPost = async () => {
  const { valid } = await formRef.value.validate()
  if (!valid) return
  if (!canSubmit.value) return

  submitting.value = true
  uploadProgress.value = 0

  try {
    const imageUrls = await uploadImages()

    const postData = {
      content: postContent.value,
      groupId: effectiveGroupId.value,
      visibility: visibility.value,
      poll: pollPayload.value
    }

    if (imageUrls.length === 1) {
      postData.imageUrl = imageUrls[0]
    }
    if (imageUrls.length > 0) {
      postData.images = imageUrls
    }

    const result = await store.dispatch('posts/createPost', postData)

    if (result.success) {
      imagePreviews.value.forEach(p => URL.revokeObjectURL(p.url))

      postContent.value = ''
      selectedImages.value = []
      imagePreviews.value = []
      selectedGroup.value = null
      visibility.value = 'public'
      uploadProgress.value = 0
      resetPoll()
      formRef.value.reset()
      emit('posted')
    }
  } catch (error) {
    console.error('Error creating post:', error)
  } finally {
    submitting.value = false
    uploadProgress.value = 0
  }
}

onMounted(() => {
  store.dispatch('groups/fetchGroups', { filter: 'my' })
})
</script>

<style scoped>
.image-previews {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 0.75rem;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
}

.preview-item {
  position: relative;
  aspect-ratio: 1;
}

.preview-item .v-img {
  width: 100%;
  height: 100%;
}

.remove-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  background: rgba(255,255,255,0.9) !important;
}

.poll-builder {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.02);
}
</style>